import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Eraser, Play, ScanSearch, Sparkles } from "lucide-react";
import {
  healthCheckApi,
  mainTemplatesApi,
  protocolsApi,
  type MainTemplate,
  type MainProtocol,
  type ProbeResult,
  type ProbeStatus,
  ApiError,
} from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";

// ---------------------------------------------------------------------------
// /health-check — 粘贴检测(IT-23)
//
// 运营粘贴一批明文 Key,选择主模板(主模板提供「主协议类型(原厂)」+ 检测模型),
// 后端并发逐条探测上游,按 active/inactive/throttled/timeout/unknown 分类展示。
// 命令:检测 / 去重 / 清空。高级设置:协议模式/代理/超时/并发/速率/提问内容/真实调用。
//
// 边界:只读、不落库、不改 Key 状态;Key 上限 100;默认并发 1;速率 20/5 每分钟。
// ---------------------------------------------------------------------------

const STATUS_META: Record<ProbeStatus, { label: string; variant: "success" | "destructive" | "warning" | "muted" }> = {
  active: { label: "存活", variant: "success" },
  inactive: { label: "失效", variant: "destructive" },
  throttled: { label: "限流", variant: "warning" },
  timeout: { label: "超时", variant: "muted" },
  unknown: { label: "未知", variant: "muted" },
};

const inpCls = "h-9 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring";

export function HealthCheckPage() {
  const [templateId, setTemplateId] = useState<number>(0);
  const [models, setModels] = useState<string[]>([]);
  const [customModel, setCustomModel] = useState("");
  const [keys, setKeys] = useState("");
  const [results, setResults] = useState<ProbeResult[] | null>(null);
  const [running, setRunning] = useState(false);
  const [pageMsg, setPageMsg] = useState<string | null>(null);

  // 高级设置
  const [advOpen, setAdvOpen] = useState(false);
  const [protocolMode, setProtocolMode] = useState("auto");
  const [proxyUrl, setProxyUrl] = useState("");
  const [timeout, setTimeoutVal] = useState(10);
  const [concurrency, setConcurrency] = useState(1);
  const [rate, setRate] = useState(20);
  const [prompt, setPrompt] = useState("echo hi");
  const [realCall, setRealCall] = useState(false);

  // 主模板(供选主协议类型 + 模型)
  const tplQ = useQuery({ queryKey: ["main-templates"], queryFn: () => mainTemplatesApi.list({ enabled: true }) });
  const templates: MainTemplate[] = tplQ.data?.items ?? [];
  const protocolsQ = useQuery({ queryKey: ["protocols"], queryFn: protocolsApi.list, staleTime: 5 * 60_000 });
  const protocols: MainProtocol[] = protocolsQ.data?.items ?? [];

  const currentTpl = templates.find((t) => t.id === templateId);
  const currentProto = currentTpl ? protocols.find((p) => p.id === currentTpl.main_protocol_id) : undefined;

  const modelOptions = useMemo(() => {
    if (!currentTpl?.models) return [];
    return currentTpl.models
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }, [currentTpl]);

  const lineCount = useMemo(
    () => keys.split("\n").map((s) => s.trim()).filter((s) => s && !s.startsWith("#")).length,
    [keys]
  );

  const stats = useMemo(() => {
    const c: Record<ProbeStatus, number> = { active: 0, inactive: 0, throttled: 0, timeout: 0, unknown: 0 };
    (results ?? []).forEach((r) => c[r.status]++);
    return { total: results?.length ?? 0, ...c };
  }, [results]);

  const parseLines = (raw: string) =>
    raw.split("\n").map((s) => s.trim()).filter((s) => s && !s.startsWith("#"));

  const toggleModel = (m: string) => {
    setModels((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  };

  const doDedup = () => {
    const lines = parseLines(keys);
    const seen = new Set<string>();
    const out: string[] = [];
    let removed = 0;
    for (const l of lines) {
      if (seen.has(l)) { removed++; continue; }
      seen.add(l); out.push(l);
    }
    setKeys(out.join("\n"));
    setPageMsg(removed > 0 ? `已去除 ${removed} 条重复 Key` : "无重复 Key");
  };

  const doClear = () => {
    setKeys("");
    setResults(null);
    setPageMsg("已清空");
  };

  const doDetect = async () => {
    const lines = parseLines(keys);
    if (lines.length === 0) { setPageMsg("请先粘贴至少一个 Key"); return; }
    if (lines.length > 100) { setPageMsg("最多 100 个 Key"); return; }
    const allModels = [...models];
    if (customModel.trim()) allModels.push(customModel.trim());
    setRunning(true);
    setResults(null);
    setPageMsg(null);
    try {
      const resp = await healthCheckApi.probe({
        template_id: templateId || undefined,
        models: allModels,
        keys: lines,
        settings: {
          protocol_mode: protocolMode === "auto" ? "" : protocolMode,
          proxy_url: proxyUrl.trim(),
          timeout_seconds: timeout,
          concurrency,
          rate_per_minute: rate,
          prompt: prompt.trim() || "echo hi",
          real_call: realCall,
        },
      });
      setResults(resp.results);
      setPageMsg(`检测完成:共 ${resp.results.length} 条`);
    } catch (e) {
      setPageMsg(e instanceof ApiError ? e.message : "检测失败");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-xl font-semibold">检测</h1>
          <p className="mt-0.5 text-xs text-muted-foreground">
            粘贴一批上游 Key,选择主模板(主协议类型(原厂)+ 检测模型),异步逐条探测存活状态。只读、不落库。
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          <ScanSearch className="h-4 w-4" />
          <span>Key 上限 100 · 默认并发 1</span>
        </div>
      </div>

      {pageMsg && (
        <div className="rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
          {pageMsg}
          <button type="button" className="ml-2 text-muted-foreground hover:text-foreground" onClick={() => setPageMsg(null)} aria-label="关闭提示">×</button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_260px]">
        {/* 左:表单 */}
        <div className="space-y-4">
          <div className="rounded-xl border bg-card p-4 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold">检测输入</h2>

            <div className="space-y-1">
              <Label>主模板 <span className="text-destructive">*</span></Label>
              <select className={inpCls} value={templateId} onChange={(e) => { setTemplateId(Number(e.target.value)); setModels([]); }}>
                <option value={0}>— 选择主模板 —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.code})</option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground">
                主协议类型(原厂)= {currentProto ? `${currentProto.name} (${currentTpl?.protocol_type})` : "—"} · 决定探测器与默认 Base URL
              </p>
            </div>

            <div className="mt-3 space-y-1">
              <Label>测试模型(来自主模板,可多选)</Label>
              {modelOptions.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">该模板未配置模型,可手填</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {modelOptions.map((m) => {
                    const on = models.includes(m);
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => toggleModel(m)}
                        className={`rounded-full border px-2.5 py-1 text-xs transition ${
                          on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/50"
                        }`}
                      >
                        {m}
                      </button>
                    );
                  })}
                </div>
              )}
              <input
                className={`${inpCls} mt-2`}
                placeholder="或手填模型 id,如 gpt-4o-mini"
                value={customModel}
                onChange={(e) => setCustomModel(e.target.value)}
              />
            </div>

            <div className="mt-3 space-y-1">
              <Label>Key 列表(每行一个)</Label>
              <textarea
                className="min-h-[180px] w-full rounded-md border bg-background px-3 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-ring"
                placeholder={"每行一个 Key\nsk-xxxxxxxxxxxx\nsk-yyyyyyyyyyyy\nAKIA...|secret|region"}
                value={keys}
                onChange={(e) => setKeys(e.target.value)}
              />
              <p className="text-[11px] text-muted-foreground">{lineCount} 行待解析 · 最多 100 个 · 支持 Bearer Token 或 AK|SK|Region</p>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <Button onClick={doDetect} disabled={running}>
                <Play className="mr-1 h-4 w-4" /> {running ? "检测中…" : "检测"}
              </Button>
              <Button variant="outline" onClick={doDedup} disabled={running}>
                <CheckCircle2 className="mr-1 h-4 w-4" /> 去重
              </Button>
              <Button variant="ghost" onClick={doClear} disabled={running}>
                <Eraser className="mr-1 h-4 w-4" /> 清空
              </Button>
            </div>

            {/* 高级设置 */}
            <div className="mt-3 rounded-lg border">
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-t-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setAdvOpen((v) => !v)}
              >
                <Sparkles className="h-4 w-4" /> 高级设置
                <span className="ml-auto text-xs text-muted-foreground">{advOpen ? "收起 ▲" : "展开 ▼"}</span>
              </button>
              {advOpen && (
                <div className="grid grid-cols-1 gap-3 border-t p-3 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label>协议模式</Label>
                    <select className={inpCls} value={protocolMode} onChange={(e) => setProtocolMode(e.target.value)}>
                      <option value="auto">自动(按协议推断)</option>
                      <option value="anthropic">Anthropic</option>
                      <option value="openai-compatible">OpenAI 兼容</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label>代理 URL(可选)</Label>
                    <input className={inpCls} placeholder="http://127.0.0.1:11304" value={proxyUrl} onChange={(e) => setProxyUrl(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label>超时秒数</Label>
                    <input type="number" min={1} max={120} className={inpCls} value={timeout} onChange={(e) => setTimeoutVal(Number(e.target.value))} />
                  </div>
                  <div className="space-y-1">
                    <Label>并发数(默认 1)</Label>
                    <input type="number" min={1} max={20} className={inpCls} value={concurrency} onChange={(e) => setConcurrency(Number(e.target.value))} />
                  </div>
                  <div className="space-y-1">
                    <Label>请求速率</Label>
                    <select className={inpCls} value={rate} onChange={(e) => setRate(Number(e.target.value))}>
                      <option value={20}>标准 · 20 个 / 分钟</option>
                      <option value={5}>保守 · 5 个 / 分钟</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label>提问内容(真实调用时发送)</Label>
                    <input className={inpCls} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" className="h-4 w-4 rounded border" checked={realCall} onChange={(e) => setRealCall(e.target.checked)} />
                      <span className="text-muted-foreground">真实模型调用(可能产生费用)</span>
                    </label>
                    <p className="text-[11px] text-muted-foreground">关闭 = 极速模式:跳过模型列表,只做最小调用并读取状态。</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 结果表 */}
          <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
            <div className="border-b px-3 py-2 text-sm font-semibold">检测结果</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/50 text-left text-xs font-medium text-muted-foreground">
                  <tr>
                    <th className="w-10 px-3 py-2.5">#</th>
                    <th className="px-3 py-2.5">Key 提示</th>
                    <th className="px-3 py-2.5">状态</th>
                    <th className="px-3 py-2.5">错误信息</th>
                    <th className="w-20 px-3 py-2.5">延迟</th>
                  </tr>
                </thead>
                <tbody>
                  {results === null ? (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">尚未检测。粘贴 Key 后点击「检测」。</td></tr>
                  ) : results.length === 0 ? (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">无结果。</td></tr>
                  ) : (
                    results.map((r, i) => {
                      const meta = STATUS_META[r.status];
                      return (
                        <tr key={i} className="border-b hover:bg-muted/30">
                          <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{r.key_index + 1}</td>
                          <td className="px-3 py-2 font-mono text-xs">{r.key_hint}</td>
                          <td className="px-3 py-2"><Badge variant={meta.variant}>{meta.label}</Badge></td>
                          <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                            {r.error_message ? `${r.error_message}${r.error_code ? ` · ${r.error_code}` : ""}` : "检测通过"}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{r.latency_ms} ms</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* 右:统计 */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <StatCard label="总计" value={stats.total} cls="text-foreground" />
            <StatCard label="存活" value={stats.active} cls="text-emerald-600" />
            <StatCard label="失效" value={stats.inactive} cls="text-red-600" />
            <StatCard label="限流" value={stats.throttled} cls="text-amber-600" />
            <StatCard label="超时" value={stats.timeout} cls="text-violet-600" />
            <StatCard label="未知" value={stats.unknown} cls="text-muted-foreground" />
          </div>
          <div className="rounded-xl border bg-card p-3 text-xs text-muted-foreground shadow-sm">
            <p className="mb-1 font-medium text-foreground">说明</p>
            检测只读探测、不落库、不落历史、不改 Key 启用状态。密钥仅发往本机后端,再由后端请求上游。结果按 HTTP 状态自动分类。
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, cls }: { label: string; value: number; cls: string }) {
  return (
    <div className="rounded-xl border bg-card p-3 text-center shadow-sm">
      <div className={`text-2xl font-bold ${cls}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}
